import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Grid, Box, makeStyles, Select, MenuItem } from "@material-ui/core";
import LazyLoad from "react-lazy-load";

import {
  getData,
  dmyUnit,
  capitalize,
  getChartTicks,
  yearFromMonths,
  avgFromArrayOfNumbers,
  onlyUniqueValuesInObject,
} from "../../../../services";
import TenancyImg from "../../../../assets/images/icons/tenancy.svg";
import RevenuesImg from "../../../../assets/images/icons/nav/revenues.svg";
import { Button } from "../../../../components/base";
import { IconTitle, NoDataCard, defaultItems, complexItems } from "../../../../components/parts";
import BellImg from "../../../../assets/images/icons/bell_black.svg";
import ProfitabilityImg from "../../../../assets/images/icons/profit.svg";
import ElectricityImg from "../../../../assets/images/icons/electricity.svg";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { TenancyChart } from "./tenancyData/tenancyChart";
import { TenancyDataInfo } from "./tenancyData/tenancyDataInfo";
import { tenancyChartLines } from "./sample";
import { TenantsList } from "./tenantList";
import { TenantDetail } from "./tenantDetail";
import { PortfolioAlert } from "./portfolioAlerts";
import { RiskProfile } from "./profitability/RiskProfile";
import { TenantPayments } from "./electricity/tenantPayments";
import { TenantConnectionStatus } from "./electricity/connectionStatus";
import { TenantPaymentsReport } from "./electricity/tenantPaymentsReport";
import { ComplexTenantsElectricityConnectionStatus } from "./electricity/ComplexTenantsElectricityConnectionStatus";
import { SupplyRevenuesTotal } from "../properties/electricity/SupplyRevenuesTotal";
import { RevenueBreakdownOverTimeComponent } from "../properties/electricity/RevenueBreakdownOverTimeComponent";
import { TenantsRebatePolicyModal } from "../../../../components/modals";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  selectRoot: {
    fontSize: 24,
    fontWeight: 700,
  },
}));

const TenancyNoDataCard = ({ title }) => (
  <NoDataCard
    title={title}
    footerNames={["In this building", "Avg. portfolio", "Avg. Industry"]}
    type="line"
  />
);

export const TenantsTab = ({ building, complex }) => {
  const classes = useStyles();
  const [tenant, setTenant] = useState();
  const [showTenantList, setShowTenantList] = useState(false);
  const [leaseSquareData, setLeaseSquareData] = useState([]);
  const [tenancyAgingData, setTenancyAgingData] = useState([]);
  const [tenancyEndData, setTenancyEndData] = useState([]);
  const [monthConsumptionData, setMonthConsumptionData] = useState([]);
  const [yearlyRevenueData, setYearlyRevenueData] = useState([]);
  const [yearlyTradingRevenueData, setYearlyTradingRevenueData] = useState([]);
  const [yearlyUnrealizedRevenueData, setYearlyUnrealizedRevenueData] = useState([]);
  const [profitabilityYearOption, setProfitabilityYearOption] = useState();
  const [profitabilityYearOptions, setProfitabilityYearOptions] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const [isOpen, setOpen] = useState(false);

  const getDataForMonthlyLeaseChart = (setChartData) => {
    const url = building
      ? `/praas/v2/buildings/${building.id}/tenants/monthly-lease/`
      : `/praas/v2/complexes/${complex.id}/tenants/monthly-lease/`;

    getData(url, (data) => {
      if (
        !data.building ||
        onlyUniqueValuesInObject(data.building, 0) ||
        onlyUniqueValuesInObject(data, null) ||
        !Object.keys(data.building).length
      ) {
        setChartData([]);
        return;
      }
      const result = {};
      ["building", "portfolio", "industry"].forEach((key) => {
        data[key].forEach((e) => {
          result[e.rent] = result[e.rent] || {};
          result[e.rent][key] = e.percent;
        });
      });

      setChartData(
        Object.keys(result).map((e) => {
          return {
            x: `${currencySymbol}${e}`,
            ...result[e],
          };
        })
      );
    });
  };

  const getDataForTenancyChart = (type, setChartData) => {
    const url = building
      ? `/praas/v2/buildings/${building.id}/tenants/${type}/`
      : `/praas/v2/complexes/${complex.id}/tenants/${type}/`;

    getData(url, (data) => {
      if (!(data.building.days.length || data.building.months.length)) {
        setChartData([]);
        return;
      }
      const result = {};
      ["building", "portfolio", "industry"].forEach((key) => {
        ["days", "months"].forEach((d) => {
          data[key][d].forEach((e) => {
            result[d[0] + e[d]] = result[d[0] + e[d]] || {};
            result[d[0] + e[d]][key] = e.percent;
          });
        });
      });

      const group = {};
      Object.keys(result).map((key) => {
        let year = null;
        if (key.includes("m")) {
          let monthWithoutM = key.replace("m", "");
          year = yearFromMonths(monthWithoutM);
        }
        if (year) {
          const yearlyKey = year * 12 + 6;
          if (!(yearlyKey in group)) {
            group["m" + yearlyKey] = [];
          }
          group["m" + yearlyKey].push(result[key]);
          delete result[key];
        } else {
          group[key] = result[key];
        }
      });

      Object.keys(group).map((key) => {
        if (Array.isArray(group[key])) {
          const bPercentages = [];
          const pPercentages = [];
          const iPercentages = [];
          group[key].forEach((item) => {
            if (item["building"]) bPercentages.push(item["building"]);
            if (item["portfolio"]) pPercentages.push(item["portfolio"]);
            if (item["industry"]) iPercentages.push(item["industry"]);
          });
          group[key] = {};
          if (bPercentages.length) group[key]["building"] = avgFromArrayOfNumbers(bPercentages);
          if (pPercentages.length) group[key]["portfolio"] = avgFromArrayOfNumbers(pPercentages);
          if (iPercentages.length) group[key]["industry"] = avgFromArrayOfNumbers(iPercentages);
        }
      });

      setChartData(
        Object.keys(group)
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          .map((e) => {
            return {
              x: dmyUnit(e, type === "tenancy-aging" ? "" : "in"),
              ...group[e],
            };
          })
      );
    });
  };

  const createChartData = (data, setChartData) => {
    if (
      !data.building ||
      onlyUniqueValuesInObject(data.building, 0) ||
      onlyUniqueValuesInObject(data, null) ||
      !Object.keys(data.building).length
    ) {
      setChartData([]);
      return;
    }
    const result = {};
    ["building", "portfolio", "industry"].forEach((key) => {
      Object.keys(data[key]).forEach((e) => {
        result[e] = result[e] || {};
        result[e][key] = data[key][e];
      });
    });
    setChartData(
      Object.keys(result).map((e) => ({
        x: `${currencySymbol}${e}`,
        ...result[e],
      }))
    );
  };

  useEffect(() => {
    getDataForMonthlyLeaseChart(setLeaseSquareData);
    getDataForTenancyChart("tenancy-aging", setTenancyAgingData);
    getDataForTenancyChart("tenancy-ends", setTenancyEndData);

    const url = building
      ? `/praas/v2/buildings/${building.id}/tenants/consumption/`
      : `/praas/v2/complexes/${complex.id}/tenants/consumption/`;

    getData(url, (data) => createChartData(data, setMonthConsumptionData));
  }, [building?.id, complex?.id]);

  useEffect(() => {
    const year = profitabilityYearOption?.label;
    const url = building
      ? `/praas/v2/buildings/${building.id}/tenants/profitability/`
      : `/praas/v2/complexes/${complex.id}/tenants/profitability/`;

    getData(
      url,
      (data) => {
        if (!profitabilityYearOptions.length) {
          setProfitabilityYearOptions(
            data.years.map((year, i) => ({
              id: i,
              label: year,
            }))
          );
        }

        createChartData(data.total_revenue, setYearlyRevenueData);
        createChartData(data.trading_revenue, setYearlyTradingRevenueData);
        createChartData(data.unrealized_revenue, setYearlyUnrealizedRevenueData);
      },
      {
        year,
      }
    );

    return () => {
      setYearlyRevenueData([]);
      setYearlyTradingRevenueData([]);
      setYearlyUnrealizedRevenueData([]);
    };
  }, [building?.id, complex?.id, profitabilityYearOption]);

  if (tenant && showTenantList)
    return (
      <TenantDetail
        building={building}
        complex={complex}
        tenant={tenant}
        setTenant={setTenant}
        setShowTenantList={setShowTenantList}
      />
    );

  if (showTenantList)
    return (
      <TenantsList id={building.id} setShowTenantList={setShowTenantList} onSelected={setTenant} />
    );

  return (
    <Box className={classes.root}>
      <Grid style={{ marginBottom: 28 }} container justifyContent="space-between">
        <IconTitle
          iconSrc={TenancyImg}
          title="TENANCY DATA & ANALYTICS"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <Box style={{ marginLeft: "auto", display: "flex" }}>
          <Box>
            {building && <Button text="TENANTS LIST" onClick={() => setShowTenantList(true)} />}
          </Box>
          <ActionButtonsGroup hideIntegrateBtn />
        </Box>
      </Grid>

      <TenancyDataInfo building={building} complex={complex} />

      <Grid container spacing={3} style={{ marginTop: 26, minHeight: 384 }}>
        <Grid item xs={4}>
          {leaseSquareData.length ? (
            <TenancyChart
              title={`Average monthly Lease/${user.unit_system}`}
              chartData={leaseSquareData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(leaseSquareData, 5)}
              footerItems={building ? defaultItems : complexItems}
            />
          ) : (
            <TenancyNoDataCard title={`Average monthly Lease/${user.unit_system}`} />
          )}
        </Grid>
        <Grid item xs={4}>
          {tenancyAgingData.length ? (
            <TenancyChart
              title="Tenancy Aging"
              chartData={tenancyAgingData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(tenancyAgingData, 4)}
              footerItems={building ? defaultItems : complexItems}
            />
          ) : (
            <TenancyNoDataCard title="Tenancy Aging" />
          )}
        </Grid>
        <Grid item xs={4}>
          {tenancyEndData.length ? (
            <TenancyChart
              title="Tenancy Ends"
              chartData={tenancyEndData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(tenancyEndData, 4)}
              footerItems={building ? defaultItems : complexItems}
            />
          ) : (
            <TenancyNoDataCard title="Tenancy Ends" />
          )}
        </Grid>
      </Grid>

      <IconTitle iconSrc={ElectricityImg} title="PORTFOLIO ELECTRICITY DATA & ANALYTICS" />

      {building ? (
        <LazyLoad>
          <TenantConnectionStatus building={building} />
        </LazyLoad>
      ) : (
        <LazyLoad>
          <ComplexTenantsElectricityConnectionStatus complex={complex} />
        </LazyLoad>
      )}

      <Grid container spacing={3} style={{ marginTop: 23, minHeight: 384 }}>
        <Grid item xs={4}>
          {monthConsumptionData.length ? (
            <TenancyChart
              title={`Tenants Avg. Monthly Consumption (kWh/${user.unit_system})`}
              chartData={monthConsumptionData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(monthConsumptionData, 5)}
              footerItems={building ? defaultItems : complexItems}
            />
          ) : (
            <TenancyNoDataCard
              title={`Tenants Avg. Monthly Consumption (kWh/${user.unit_system})`}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          <TenantPayments building={building} complex={complex} />
        </Grid>
        <Grid item xs={4}>
          <TenantPaymentsReport building={building} complex={complex} />
        </Grid>
      </Grid>

      <Grid style={{ paddingTop: 25 }} container justifyContent="space-between" alignItems="center">
        <IconTitle
          iconSrc={RevenuesImg}
          title="PRaaS Electricity Supply Revenues"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <Box style={{ marginLeft: "auto", height: "fit-content" }}>
          <Button text="TENANTS REBATE POLICY" onClick={() => setOpen(true)} />
        </Box>
        <TenantsRebatePolicyModal
          isOpen={isOpen}
          setOpen={() => setOpen(false)}
          building={building}
        />
      </Grid>
      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item lg={4}>
          <LazyLoad height="100%">
            <SupplyRevenuesTotal
              buildingId={building?.id}
              complexId={complex?.id}
              title={"Revenues - Total"}
            />
          </LazyLoad>
        </Grid>
        <Grid item lg={8}>
          <LazyLoad height="100%">
            <RevenueBreakdownOverTimeComponent buildingId={building?.id} complexId={complex?.id} />
          </LazyLoad>
        </Grid>
      </Grid>

      <Grid container>
        <IconTitle
          iconSrc={ProfitabilityImg}
          title="TENANTS PROFITABILITY"
          rootStyle={{
            marginRight: 10,
          }}
        />
        {profitabilityYearOptions?.length ? (
          <Select
            labelId="label"
            id="select"
            defaultValue={profitabilityYearOptions[0].id}
            autoWidth={true}
            disableUnderline
            classes={{ root: classes.selectRoot }}
            onChange={(e) => setProfitabilityYearOption(profitabilityYearOptions[e.target.value])}
          >
            {profitabilityYearOptions.map((option, i) => (
              <MenuItem key={i} value={option.id} style={{ fontSize: 12 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </Grid>

      <Grid container spacing={3} style={{ minHeight: 384 }}>
        <Grid item xs={4}>
          {yearlyRevenueData.length ? (
            <TenancyChart
              title={`Total Yearly Revenue/${capitalize(user.unit_system)}`}
              chartData={yearlyRevenueData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(yearlyRevenueData, 3)}
            />
          ) : (
            <TenancyNoDataCard title={`Total Yearly Revenue/${capitalize(user.unit_system)}`} />
          )}
        </Grid>
        <Grid item xs={4}>
          {yearlyTradingRevenueData.length ? (
            <TenancyChart
              title={`PRaaS Trading Revenues/${capitalize(user.unit_system)} - per year`}
              chartData={yearlyTradingRevenueData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(yearlyTradingRevenueData, 3)}
            />
          ) : (
            <TenancyNoDataCard
              title={`PRaaS Trading Revenues/${capitalize(user.unit_system)} - per year`}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {yearlyUnrealizedRevenueData.length ? (
            <TenancyChart
              title={`Unrealized PRaaS Revenues/${capitalize(user.unit_system)} - per year`}
              chartData={yearlyUnrealizedRevenueData}
              chartLines={tenancyChartLines}
              ticks={getChartTicks(yearlyUnrealizedRevenueData, 3)}
            />
          ) : (
            <TenancyNoDataCard
              title={`Unrealized PRaaS Revenues/${capitalize(user.unit_system)} - per year`}
            />
          )}
        </Grid>
      </Grid>

      <LazyLoad>
        <RiskProfile building={building} complex={complex} />
      </LazyLoad>

      <IconTitle iconSrc={BellImg} title="PORTFOLIO ALERTS" />

      <LazyLoad>
        <PortfolioAlert building={building} complex={complex} />
      </LazyLoad>
    </Box>
  );
};
